import { Box, Button, Flex, Image, Modal, TextInput, NoAccess } from "@dehub/theme";
import type { AsyncDataTableRef } from "@dehub/theme";
import { MagnifyingGlassIcon, PlusIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { BusinessApi } from "@/apis";
import type { IAuth } from "@/models/Auth";
import type { IBusinessListRow } from "@/models/Business";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission, TransCode } from "@/shared/a11y/trans-code";
import { useUnsavedGuard } from "@/shared/a11y/use-unsaved-guard";
import { PageLayout } from "@/shared/layout";
import { message } from "@/utils/message";
import { BusinessDrawer } from "./form/BusinessDrawer";
import { BusinessUsersDrawer } from "./users/BusinessUsersDrawer";
import { openActivateConfirm } from "./components/ActivateConfirm";
import { BusinessNotice } from "./components/BusinessNotice";
import { BusinessTable } from "./components/BusinessTable";
import { BusinessTypeSwitch } from "./components/BusinessTypeSwitch";
import { createBusinessColumns } from "./components/business-columns";
import { BUSINESS_PERMISSION_CODES, useBusinessListAccess, useBusinessPermissions } from "./permissions";
import {
  BUSINESS_TYPE_PARAM,
  parseTypeParam,
  pickBusinessRecord,
  type BusinessRecord,
  type BusinessType,
  type DrawerMode,
  type RowActionKey,
} from "./types";
import classes from "./business.module.css";

type FormDrawerState = { mode: DrawerMode; record?: BusinessRecord; key: number };
type UsersDrawerState = { record: BusinessRecord; key: number };

const SEARCH_DEBOUNCE_MS = 300;

export function BusinessPage() {
  const { t } = useTranslation("business");
  const { t: tCommon } = useTranslation("common");
  const [params, setParams] = useSearchParams();
  const user = useSelector((state: { auth: IAuth }) => state.auth.user);
  const businessStatus = useSelector((state: { general: IGeneral }) => state.general.businessStatus);

  const access = useBusinessListAccess();
  const requested = parseTypeParam(params.get("type"));
  // The URL decides the type, unless that type is not permitted: then the first permitted one.
  const type: BusinessType | null =
    requested && access[requested] ? requested : access.SUPPLIER ? "SUPPLIER" : access.BUYER ? "BUYER" : null;
  const perms = useBusinessPermissions(type ?? "SUPPLIER");

  // type, search text (?q=) and page (?page=) all live in the URL: a reload or a shared link lands on the same list.
  const query = params.get("q") ?? "";
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const updateParams = (patch: Record<string, string | null>, replace = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace },
    );

  useEffect(() => {
    if (type && requested !== type) updateParams({ type: BUSINESS_TYPE_PARAM[type] }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, requested]);

  const [queryInput, setQueryInput] = useState(query);
  // Typing is debounced into ?q=; a new search starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = queryInput.trim();
      if (next !== query) updateParams({ q: next || null, page: null }, true);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput]);
  // Back/forward changes ?q=: the box follows it.
  useEffect(() => setQueryInput(query), [query]);

  const tableRef = useRef<AsyncDataTableRef | null>(null);
  const reload = () => tableRef.current?.reload();
  const openCount = useRef(0);
  const [formDrawer, setFormDrawer] = useState<(FormDrawerState & { opened: boolean }) | null>(null);
  const [usersDrawer, setUsersDrawer] = useState<(UsersDrawerState & { opened: boolean }) | null>(null);
  const [previewLogo, setPreviewLogo] = useState<{ src: string; name: string } | null>(null);
  // The row button that opened a drawer, so focus can go back to it when the drawer closes.
  const openerRef = useRef<string | null>(null);
  const [drawerDirty, setDrawerDirty] = useState(false);
  // Unsaved drawer edits: warn on reload/tab close and on navigation, which includes switching the type
  // (it changes the URL) and the browser back button.
  useUnsavedGuard(drawerDirty);

  const restoreFocus = () => {
    const anchor = openerRef.current;
    if (!anchor) return;
    setTimeout(() => document.querySelector<HTMLElement>(`[data-row-action="${anchor}"]`)?.focus(), 80);
  };

  // A drawer belongs to one type: switching the type closes it.
  useEffect(() => {
    setFormDrawer((d) => (d ? { ...d, opened: false } : d));
    setUsersDrawer((d) => (d ? { ...d, opened: false } : d));
  }, [type]);

  // The backend refuses writes (update, profile, status) for a partner without an address. Those actions
  // are disabled with the reason; viewing a business and its users still works.
  const addressReason = user?.partner?.province ? null : t("addressError");

  const openForm = (mode: DrawerMode, record?: BusinessRecord, anchor?: string) => {
    openerRef.current = anchor ?? (record ? `${record.id}:${mode === "DETAIL" ? "detail" : "edit"}` : "add");
    openCount.current += 1;
    setFormDrawer({ mode, record, key: openCount.current, opened: true });
  };

  const openUsers = (record: BusinessRecord) => {
    openerRef.current = `${record.id}:users`;
    openCount.current += 1;
    setUsersDrawer({ record, key: openCount.current, opened: true });
  };

  const handleActivate = (record: BusinessRecord, activate: boolean) => {
    if (!type) return;
    openActivateConfirm({
      t,
      tCommon,
      record,
      type,
      activate,
      onConfirm: async () => {
        try {
          await BusinessApi.activate(record.id, activate);
          message.success(t(activate ? "activate.successOn" : "activate.successOff"));
          reload();
        } catch (error: unknown) {
          message.error((error as { message?: string })?.message ?? tCommon("error.generic"));
        }
      },
    });
  };

  const handleRowAction = (key: RowActionKey, row: IBusinessListRow) => {
    const record = pickBusinessRecord(row);
    if (key === "edit") openForm("EDIT", record);
    else if (key === "detail") openForm("DETAIL", record);
    else if (key === "users") openUsers(record);
    else handleActivate(record, key === "activate");
  };

  const columns = useMemo(
    () =>
      createBusinessColumns({
        type: type ?? "SUPPLIER",
        perms,
        businessStatus,
        addressReason,
        t,
        tCommon,
        onRowAction: handleRowAction,
        onLogoPreview: (src, name) => setPreviewLogo({ src, name }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, perms.businessEdit.hasAccess, perms.businessDetail.hasAccess, perms.userList.hasAccess, perms.changeStatus.hasAccess, businessStatus, addressReason, t, tCommon],
  );

  const breadcrumbs = [{ to: "/", label: tCommon("home") }, { label: t("page.title") }];
  // Adding creates the business (ADD) and the drawer's first step is the main information (MAIN).
  const addMissing = [!perms.add.hasAccess ? perms.codes.add : null, !perms.main.hasAccess ? perms.codes.main : null]
    .filter(Boolean)
    .join(", ");
  const addDisabledReason = !type ? null : addMissing ? <MissingPermission code={addMissing} /> : addressReason;

  const addButton = type ? (
    <ReasonTooltip key="add" reason={addDisabledReason}>
      <Button data-row-action="add" className={classes.btn} leftSection={<PlusIcon size={16} weight="bold" />} onClick={() => openForm("ADD")}>
        {t(`addBtn.${type}`)}
      </Button>
    </ReasonTooltip>
  ) : null;

  return (
    <PageLayout
      title={t("page.title")}
      subTitle={t("page.subtitle")}
      breadcrumb={breadcrumbs}
      extra={addButton ? [addButton] : []}
    >
      <Flex direction="column" gap="md" className={`${classes.tokens} ${classes.page}`}>
        {type ? (
          <>
            <BusinessNotice type={type} />
            <Box className={classes.toolbar}>
              <BusinessTypeSwitch
                value={type}
                access={access}
                onChange={(next) => setParams({ type: BUSINESS_TYPE_PARAM[next] })}
              />
              <TextInput
                className={classes.search}
                aria-label={t("searchLabel")}
                type="search"
                name="q"
                autoComplete="off"
                placeholder={t("searchPlaceholder")}
                leftSection={<MagnifyingGlassIcon size={16} aria-hidden="true" />}
                value={queryInput}
                onChange={(event) => setQueryInput(event.currentTarget.value)}
              />
            </Box>
            <BusinessTable
              ref={tableRef}
              type={type}
              query={query}
              page={page}
              onPageChange={(next) => updateParams({ page: next > 1 ? String(next) : null })}
              columns={columns}
              onAdd={() => openForm("ADD")}
              addReason={addDisabledReason}
            />
          </>
        ) : (
          <NoAccess
            title={t("noAccess.title")}
            description={
              <TransCode
                i18nKey="business:noAccess.text"
                values={{
                  codes: `${BUSINESS_PERMISSION_CODES.SUPPLIER.businessList}, ${BUSINESS_PERMISSION_CODES.BUYER.businessList}`,
                }}
              />
            }
          />
        )}
      </Flex>

      {type && formDrawer ? (
        <BusinessDrawer
          key={formDrawer.key}
          opened={formDrawer.opened}
          mode={formDrawer.mode}
          type={type}
          record={formDrawer.record}
          perms={perms}
          onClose={() => {
            setFormDrawer((d) => (d ? { ...d, opened: false } : d));
            restoreFocus();
          }}
          onDirtyChange={setDrawerDirty}
          onChanged={reload}
        />
      ) : null}

      {type && usersDrawer ? (
        <BusinessUsersDrawer
          key={usersDrawer.key}
          opened={usersDrawer.opened}
          type={type}
          record={usersDrawer.record}
          perms={perms}
          onClose={() => {
            setUsersDrawer((d) => (d ? { ...d, opened: false } : d));
            reload();
            restoreFocus();
          }}
        />
      ) : null}

      <Modal
        title={false}
        aria-label={previewLogo?.name}
        opened={previewLogo !== null}
        onClose={() => setPreviewLogo(null)}
        padding={0}
        withCloseButton={false}
        centered
        radius="lg"
      >
        {previewLogo ? <Image src={previewLogo.src} alt={previewLogo.name} /> : null}
      </Modal>
    </PageLayout>
  );
}
